import React, { useEffect, useRef, useState, ReactNode } from 'react';
import Toaster from 'components/Toaster';
import { Toast } from './types';

interface IToasterContext {
  info: (title: string, msg: string) => void;
  success: (title: string, msg: string) => void;
  error: (title: string, msg: ReactNode | { key: string; errors: string[] }[]) => void;
}

export const ToastContext = React.createContext<IToasterContext>({
  info: () => {},
  success: () => {},
  error: () => {},
});

interface Props {
  children?: React.ReactNode;
}

const ToastProvider = (props: Props) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Record<string, number>>({});

  const generateToastId = () => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const clearTimer = (id: string) => {
    const timerId = timersRef.current[id];
    if (timerId) {
      clearTimeout(timerId);
      delete timersRef.current[id];
    }
  };

  const removeToastById = (id: string) => {
    clearTimer(id);
    setToasts((currentToasts) => currentToasts.filter((t) => t.id !== id));
  };

  const addToast = (toast: Toast, autoDismissMs?: number) => {
    setToasts((currentToasts) => [...currentToasts, toast]);
    if (toast.timed && autoDismissMs && autoDismissMs > 0) {
      timersRef.current[toast.id] = window.setTimeout(() => {
        removeToastById(toast.id);
      }, autoDismissMs);
    }
  };

  const info = (title: string, msg: string) => {
    const id = generateToastId();
    const infoToast: Toast = {
      id,
      type: 'info',
      title: title,
      message: msg,
      dismissible: true,
      timed: true,
    };
    addToast(infoToast, 700);
  };

  const success = (title: string, msg: string) => {
    const id = generateToastId();
    const successToast: Toast = {
      id,
      type: 'success',
      title: title,
      message: msg,
      dismissible: true,
      timed: true,
    };
    addToast(successToast, 700);
  };

  const error = (title: string, msg: ReactNode) => {
    const id = generateToastId();
    const dangerToast: Toast = {
      id,
      type: 'danger',
      title: title,
      message: msg,
      dismissible: true,
      timed: false,
    };
    addToast(dangerToast);
  };

  const handleCloseToast = (index: number) => {
    const toast = toasts[index];
    if (toast?.id) {
      removeToastById(toast.id);
      return;
    }
    setToasts((currentToasts) => currentToasts.filter((_, i) => i !== index));
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((timerId) => clearTimeout(timerId));
      timersRef.current = {};
    };
  }, []);

  return (
    <ToastContext.Provider value={{ info, error, success }}>
      <Toaster toasts={toasts} handleCloseToast={handleCloseToast} />
      {props.children}
    </ToastContext.Provider>
  );
};
export default ToastProvider;
